import { ChangeEvent, useRef } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  PURCHASE_ORDER_CHAIN_LABEL,
  PurchaseOrderChain,
} from "../../types/purchase-order.type";
import {
  ACCEPTED_FILE_TYPES,
  CARD_CONTENT_SX,
  CARD_SX,
  CHAIN_OPTIONS,
  IMPORTABLE_CHAINS,
} from "./constants";

interface SelectFileCardProps {
  chain: PurchaseOrderChain;
  /** ชื่อไฟล์ที่ผ่านการตรวจฝั่ง browser แล้ว · null = ยังไม่ได้เลือก */
  fileName: string | null;
  checking: boolean;
  /** เหตุผลที่ไฟล์ยังใช้ไม่ได้ ทั้งจากการตรวจฝั่ง browser และจาก backend */
  failure: string | null;
  onChangeChain: (chain: PurchaseOrderChain) => void;
  onSelectFile: (event: ChangeEvent<HTMLInputElement>) => void;
  onCheck: () => void;
}

/**
 * ขั้นที่ 1 ของ W2 — เลือกเชนและไฟล์ แล้วสั่งตรวจ
 *
 * ปุ่ม "ตรวจไฟล์" เรียก preview ซึ่งยังไม่เขียนอะไรลงฐานข้อมูล ระบบพักผลตรวจไว้
 * 30 นาที และขั้นยืนยันเท่านั้นที่เขียนจริง
 */
const SelectFileCard = ({
  chain,
  fileName,
  checking,
  failure,
  onChangeChain,
  onSelectFile,
  onCheck,
}: SelectFileCardProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenFilePicker = () => {
    inputRef.current?.click();
  };

  const handleChangeChain = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = CHAIN_OPTIONS.find(
      (option) => option === event.target.value,
    );
    if (selected) onChangeChain(selected);
  };

  return (
    <Card sx={CARD_SX}>
      <CardContent sx={CARD_CONTENT_SX}>
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            เลือกเชนและไฟล์ PO (.xlsx/.xls) ขนาดไม่เกิน 10MB
          </Typography>

          <Divider />

          <TextField
            select
            size="small"
            label="เชน"
            value={chain}
            onChange={handleChangeChain}
            disabled={checking}
            sx={{ maxWidth: 320 }}
          >
            {CHAIN_OPTIONS.map((option) => (
              <MenuItem
                key={option}
                value={option}
                disabled={!IMPORTABLE_CHAINS.includes(option)}
              >
                {PURCHASE_ORDER_CHAIN_LABEL[option]}
              </MenuItem>
            ))}
          </TextField>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            onChange={onSelectFile}
            style={{ display: "none" }}
          />

          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleOpenFilePicker}
              disabled={checking}
              sx={{ whiteSpace: "nowrap" }}
            >
              เลือกไฟล์
            </Button>

            <Button
              variant="contained"
              onClick={onCheck}
              loading={checking}
              disabled={!fileName}
              sx={{ whiteSpace: "nowrap" }}
            >
              ตรวจไฟล์
            </Button>

            {fileName && (
              <Typography variant="body2" color="text.secondary">
                ไฟล์ที่เลือก: {fileName}
              </Typography>
            )}
          </Stack>

          {failure && (
            <Alert severity="error">
              <AlertTitle sx={{ mb: 0 }}>ไฟล์นี้ยังใช้ไม่ได้</AlertTitle>
              {failure}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SelectFileCard;
